import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import {
    Box,
    Stack,
    HStack,
    Button,
    Center,
    Flex,
    Heading,
    Spinner,
    useToast
} from '@chakra-ui/react';
import Form from '@rjsf/chakra-ui';
import validator from "@rjsf/validator-ajv8";
import { reverseTransformData, transformData } from '../../util/helper';
import ImageWidget from '../../CommonComponents/ImageWidget';
import { createNewEvent, getEventData, updateEventData } from '../../services/EventList';
import { handleApiError, showSuccessToast } from '../../util/errorHandler';

// Left Column Schema - Basic Event Fields
const leftColumnSchema = (alias) => ({
    "title": "Event Fields",
    "description": "",
    "type": "object",
    "properties": {
        "_id": {
            "type": "string",
            "title": "_id"
        },
        alias: {
            "type": "string",
            "title": "Alias",
            "default": ""
        },
        event_cover_images: {
            type: "array",
            title: "Event Cover Images",
            items: { type: "object", properties: { image_alias: { type: "string", default: "", title: "Image Path" }, image: { type: "string", default: "", title: "Image" } } },
            "default": []
        },
        canto_cover_id: {
            "type": "string",
            "title": "Canto Cover ID",
            "default": ""
        },
        event_title: {
            "type": "string",
            "title": "Event Title",
            "default": ""
        },
        event_sub_title: {
            "type": "string",
            "title": "Event Sub Title",
            "default": ""
        },
        collaboration_label: {
            "type": "string",
            "title": "Collaboration Label",
            "default": ""
        },
        collaboration_detail: {
            "type": "string",
            "title": "Collaboration Detail",
            "default": ""
        },
        description: {
            "type": "string",
            "title": "Description",
            "default": ""
        },
        format: {
            "type": "string",
            "title": "Format",
            "default": ""
        },
        fee: {
            "type": "string",
            "title": "Fee",
            "default": ""
        },
        venue: {
            "type": "string",
            "title": "Venue",
            "default": ""
        },
        event_map_link: {
            "type": "string",
            "title": "Event Map Link",
            "default": ""
        }
    },
    required: ['alias', 'event_title']
});

// Right Column Schema - Date & Reference Fields
const rightColumnSchema = () => ({
    "title": "",
    "description": "",
    "type": "object",
    "properties": {
        sortable_day_start: {
            "type": "integer",
            "title": "Sortable Day Start",
            "default": 1
        },
        sortable_month_start: {
            "type": "integer",
            "title": "Sortable Month Start",
            "default": 1
        },
        sortable_year_start: {
            "type": "integer",
            "title": "Sortable Year Start",
            "default": 2023
        },
        actual_date_start: {
            "type": "string",
            "title": "Actual Date Start",
            "default": ""
        },
        sortable_day_end: {
            "type": "integer",
            "title": "Sortable Day End",
            "default": 1
        },
        sortable_month_end: {
            "type": "integer",
            "title": "Sortable Month End",
            "default": 1
        },
        sortable_year_end: {
            "type": "integer",
            "title": "Sortable Year End",
            "default": 2023
        },
        actual_date_end: {
            "type": "string",
            "title": "Actual Date End",
            "default": ""
        },
        event_duration: {
            "type": "string",
            "title": "Event Duration",
            "default": ""
        },
        post_alias: {
            "type": "string",
            "title": "Post Alias",
            "default": ""
        },
        project_alias: {
            "type": "string",
            "title": "Project Alias",
            "default": ""
        },
        event_template_id: {
            "type": "string",
            "title": "Event Template ID",
            "default": ""
        }
    }
});

// Left Column UI Schema
const leftColumnUiSchema = (alias, isSubmitting) => ({
    "ui:submitButtonOptions": {
        "norender": true
    },
    "ui:style": {
        fontSize: "14px",
    },
    "_id": {
        "ui:widget": "hidden"
    },
    alias: {
        'ui:emptyValue': '',
        'ui:readonly': !!alias
    },
    event_cover_images: {
        "ui:emptyValue": [],
        "items": {
            image_alias: { "ui:emptyValue": "" },
            image: { "ui:widget": ImageWidget, "ui:emptyValue": "" }
        }
    },
    description: {
        "ui:widget": "textarea",
        "ui:options": {
            rows: 5
        },
        "ui:emptyValue": ""
    },
    canto_cover_id: { "ui:emptyValue": "" },
    event_title: { "ui:emptyValue": "" },
    event_sub_title: { "ui:emptyValue": "" },
    collaboration_label: { "ui:emptyValue": "" },
    collaboration_detail: { "ui:emptyValue": "" },
    format: { "ui:emptyValue": "" },
    fee: { "ui:emptyValue": "" },
    venue: { "ui:emptyValue": "" },
    event_map_link: { "ui:emptyValue": "" }
});

// Right Column UI Schema
const rightColumnUiSchema = (isSubmitting) => ({
    "ui:submitButtonOptions": {
        "norender": true
    },
    "ui:style": {
        fontSize: "14px",
    },
    sortable_day_start: { "ui:emptyValue": 1 },
    sortable_month_start: { "ui:emptyValue": 1 },
    sortable_year_start: { "ui:emptyValue": 2023 },
    actual_date_start: { "ui:emptyValue": "" },
    sortable_day_end: { "ui:emptyValue": 1 },
    sortable_month_end: { "ui:emptyValue": 1 },
    sortable_year_end: { "ui:emptyValue": 2023 },
    actual_date_end: { "ui:emptyValue": "" },
    event_duration: { "ui:emptyValue": "" },
    post_alias: { "ui:emptyValue": "" },
    project_alias: { "ui:emptyValue": "" },
    event_template_id: { "ui:emptyValue": "" }
});

// Combined Schema for Single Column (Preview Mode)
const combinedSchema = (alias) => ({
        "title": "Event Fields",
        "description": "",
        "type": "object",
        "properties": {
        "_id": {
            "type": "string",
            "title": "_id"
        },
            alias: {
                "type": "string",
                "title": "Alias",
                "default": ""
            },
            event_cover_images: {
                type: "array",
                title: "Event Cover Images",
            items: { type: "object", properties: { image_alias: { type: "string", default: "", title: "Image Path" }, image: { type: "string", default: "", title: "Image" } } },
            "default": []
            },
            canto_cover_id: {
                "type": "string",
                "title": "Canto Cover ID",
                "default": ""
            },
            event_title: {
                "type": "string",
                "title": "Event Title",
                "default": ""
            },
            event_sub_title: {
                "type": "string",
                "title": "Event Sub Title",
                "default": ""
            },
            collaboration_label: {
                "type": "string",
                "title": "Collaboration Label",
                "default": ""
            },
            collaboration_detail: {
                "type": "string",
                "title": "Collaboration Detail",
                "default": ""
            },
            description: {
                "type": "string",
                "title": "Description",
                "default": ""
            },
            format: {
                "type": "string",
                "title": "Format",
                "default": ""
            },
            fee: {
                "type": "string",
                "title": "Fee",
                "default": ""
            },
            venue: {
                "type": "string",
                "title": "Venue",
                "default": ""
            },
            event_map_link: {
                "type": "string",
                "title": "Event Map Link",
                "default": ""
            },
            sortable_day_start: {
                "type": "integer",
                "title": "Sortable Day Start",
                "default": 1
            },
            sortable_month_start: {
                "type": "integer",
                "title": "Sortable Month Start",
                "default": 1
            },
            sortable_year_start: {
                "type": "integer",
                "title": "Sortable Year Start",
                "default": 2023
            },
            actual_date_start: {
                "type": "string",
                "title": "Actual Date Start",
                "default": ""
            },
            sortable_day_end: {
                "type": "integer",
                "title": "Sortable Day End",
                "default": 1
            },
            sortable_month_end: {
                "type": "integer",
                "title": "Sortable Month End",
                "default": 1
            },
            sortable_year_end: {
                "type": "integer",
                "title": "Sortable Year End",
                "default": 2023
            },
            actual_date_end: {
                "type": "string",
                "title": "Actual Date End",
                "default": ""
            },
            event_duration: {
                "type": "string",
                "title": "Event Duration",
                "default": ""
            },
            post_alias: {
                "type": "string",
                "title": "Post Alias",
                "default": ""
            },
            project_alias: {
                "type": "string",
                "title": "Project Alias",
                "default": ""
            },
            event_template_id: {
                "type": "string",
                "title": "Event Template ID",
                "default": ""
            }
        },
        required: ['alias', 'event_title']
});

// Combined UI Schema for Single Column (Preview Mode)
const combinedUiSchema = (alias, isSubmitting) => ({
    "ui:submitButtonOptions": {
        "norender": true
    },
    "ui:style": {
        fontSize: "14px",
    },
    "_id": {
        "ui:widget": "hidden"
    },
        alias: {
            'ui:emptyValue': '',
            'ui:readonly': !!alias
        },
        event_cover_images: {
        "ui:emptyValue": [],
            "items": {
                image_alias: { "ui:emptyValue": "" },
                image: { "ui:widget": ImageWidget, "ui:emptyValue": "" }
            }
        },
        description: {
            "ui:widget": "textarea",
            "ui:options": {
                rows: 5
        },
        "ui:emptyValue": ""
    },
    canto_cover_id: { "ui:emptyValue": "" },
    event_title: { "ui:emptyValue": "" },
    event_sub_title: { "ui:emptyValue": "" },
    collaboration_label: { "ui:emptyValue": "" },
    collaboration_detail: { "ui:emptyValue": "" },
    format: { "ui:emptyValue": "" },
    fee: { "ui:emptyValue": "" },
    venue: { "ui:emptyValue": "" },
    event_map_link: { "ui:emptyValue": "" },
    sortable_day_start: { "ui:emptyValue": 1 },
    sortable_month_start: { "ui:emptyValue": 1 },
    sortable_year_start: { "ui:emptyValue": 2023 },
    actual_date_start: { "ui:emptyValue": "" },
    sortable_day_end: { "ui:emptyValue": 1 },
    sortable_month_end: { "ui:emptyValue": 1 },
    sortable_year_end: { "ui:emptyValue": 2023 },
    actual_date_end: { "ui:emptyValue": "" },
    event_duration: { "ui:emptyValue": "" },
    post_alias: { "ui:emptyValue": "" },
    project_alias: { "ui:emptyValue": "" },
    event_template_id: { "ui:emptyValue": "" }
});

const Event = () => {
    const { alias } = useParams();
    const [leftFormData, setLeftFormData] = useState({});
    const [rightFormData, setRightFormData] = useState({});
    const [combinedFormData, setCombinedFormData] = useState({});
    const [quickSave, setQuickSave] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const toast = useToast();
    const navigate = useNavigate();
    const previewEnabled = useSelector(state => state.preview.isEnabled);

    // Split data for two-column layout
    const splitFormData = (data = {}) => {
        const leftFields = ['_id', 'alias', 'event_cover_images', 'canto_cover_id', 'event_title', 'event_sub_title', 'collaboration_label', 'collaboration_detail', 'description', 'format', 'fee', 'venue', 'event_map_link'];
        const rightFields = ['sortable_day_start', 'sortable_month_start', 'sortable_year_start', 'actual_date_start', 'sortable_day_end', 'sortable_month_end', 'sortable_year_end', 'actual_date_end', 'event_duration', 'post_alias', 'project_alias', 'event_template_id'];
        
        const leftData = {};
        const rightData = {};
        
        // Process left fields with defaults
        leftFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                leftData[field] = data[field];
            } else {
                // Set appropriate defaults
                if (field === 'event_cover_images') leftData[field] = [];
                else leftData[field] = "";
            }
        });
        
        // Process right fields with defaults
        rightFields.forEach(field => {
            if (data.hasOwnProperty(field)) {
                rightData[field] = data[field];
            } else {
                // Set appropriate defaults
                if (field.includes('sortable_day') || field.includes('sortable_month')) rightData[field] = 1;
                else if (field.includes('sortable_year')) rightData[field] = 2023;
                else rightData[field] = "";
            }
        });
        
        return { leftData, rightData };
    };
    
    // Combine data from both forms
    const combineFormData = (leftData, rightData) => {
        return { ...leftData, ...rightData };
    };

    // Handle form changes for two-column layout
    const handleLeftFormChange = (data) => {
        setLeftFormData(data.formData);
    };
    
    const handleRightFormChange = (data) => {
        setRightFormData(data.formData);
    };

    const handleCombinedFormChange = (data) => {
        setCombinedFormData(data.formData);
    };

    // Fetch initial data
    useEffect(() => {
        const getBasicData = async () => {
            try {
                if (alias) {
                    const result = await getEventData(alias);
                    if (result && result?.data?.[0]) {
                        const fieldsToTransform = ['event_cover_images'];
                        const transformedData = transformData(result?.data[0], fieldsToTransform);
                        
                        // Split data for two-column layout
                        const { leftData, rightData } = splitFormData(transformedData);
                        setLeftFormData(leftData);
                        setRightFormData(rightData);
                        setCombinedFormData(transformedData);
                    }
                } else {
                    // For new events, initialize empty form data
                    const emptyData = {};
                    const { leftData, rightData } = splitFormData(emptyData);
                    setLeftFormData(leftData);
                    setRightFormData(rightData);
                    setCombinedFormData(emptyData);
                }
            } catch (error) {
                handleApiError(error, toast, {
                    defaultErrorMessage: "Failed to fetch event data"
                });
            } finally {
                setIsLoading(false);
            }
        };

        getBasicData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alias, quickSave]);

    // Submit updated data
    const handleSubmit = async (formData = null) => {
        // Use combined form data in preview mode, otherwise combine left and right forms
        const finalFormData = previewEnabled ? combinedFormData : combineFormData(leftFormData, rightFormData);
        
        // Validate required fields
        if (!finalFormData.alias || !finalFormData.event_title) {
            return;
        }

        const updatedFormData = reverseTransformData(finalFormData, ['event_cover_images']);

        try {
            if (alias) {
                setIsSubmitting(true)
                const result = await updateEventData(alias, updatedFormData);

                if (result?.success) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "Event updated successfully");
                }
            } else {
                setIsSubmitting(true);
                const result = await createNewEvent(updatedFormData);
                if (result?.status === 201) {
                    setQuickSave(prev => !prev);
                    showSuccessToast(toast, "New event created successfully");
                    navigate(`/events/${result?.data?.alias}`);
                }
            }
        } catch (error) {
            handleApiError(error, toast);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <Box maxW="auto">
            {isLoading ? (
                <Center h="400px">
                    <Flex direction="column" align="center">
                        <Spinner
                            thickness="4px"
                            speed="0.65s"
                            emptyColor="gray.200"
                            color="teal.500"
                            size="xl"
                            mb={4}
                        />
                        <Heading size="md" color="gray.600">Loading data...</Heading>
                    </Flex>
                </Center>
            ) : (
                <Box>
                    {previewEnabled ? (
                        /* Single column layout for preview mode */
                        <Box>
                            <Form
                                schema={combinedSchema(alias)}
                                uiSchema={combinedUiSchema(alias, isSubmitting)}
                                formData={combinedFormData}
                                validator={validator}
                                onChange={handleCombinedFormChange}
                                fontsize={"14px"}
                            />
                        </Box>
                    ) : (
                        /* Two column layout with separate forms */
                        <Box>
                            <Stack flexDirection="row" gap={4} alignItems="flex-start">
                                <HStack flex={1}>
                                    <Box width="100%" bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                                        <Box mb={4} fontWeight="bold" color="blue.800">Column 1 - Basic Event Fields</Box>
                                        <Form
                                            schema={leftColumnSchema(alias)}
                                            uiSchema={leftColumnUiSchema(alias, isSubmitting)}
                                            formData={leftFormData}
                                            validator={validator}
                                            onChange={handleLeftFormChange}
                                            fontsize={"14px"}
                                        />
                                    </Box>
                                </HStack>
                                <HStack flex={1}>
                                    <Box width="100%" bg="white" border="1px solid" borderColor="gray.200" p={4} borderRadius="md">
                <Form
                                            schema={rightColumnSchema()}
                                            uiSchema={rightColumnUiSchema(isSubmitting)}
                                            formData={rightFormData}
                    validator={validator}
                                            onChange={handleRightFormChange}
                                            fontsize={"14px"}
                                        />
                                    </Box>
                                </HStack>
                            </Stack>
                        </Box>
                    )}
                </Box>
            )}
            
            {/* Fixed Save Button at Bottom */}
            <Box
                position="fixed"
                bottom={0}
                left="250px" // Account for sidebar width
                right={0}
                bg="white"
                borderTop="1px solid"
                borderColor="gray.200"
                p={4}
                zIndex={1000}
                display="flex"
            >
                <Box maxW="auto" display="flex" mx="auto">
                    <Button
                        colorScheme="blue"
                        size="lg"
                        width="auto"
                        isLoading={isSubmitting}
                        loadingText="Saving..."
                        onClick={() => handleSubmit()}
                        disabled={isSubmitting}
                    >
                        {isSubmitting ? 'Saving...' : 'Save'}
                    </Button>
                </Box>
            </Box>
            
            {/* Add padding to prevent content from being hidden behind fixed button */}
            <Box pb="100px" />
        </Box>
    );
};

export default Event; 